import { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchOverview } from '../api/client';
import { useAuth } from '../context/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/games', label: 'Games', icon: '🏟️' },
  { path: '/signals', label: 'Signals', icon: '📡' },
  { path: '/opportunities', label: 'Opportunities', icon: '🎯' },
  { path: '/bets', label: 'Bets', icon: '💰' },
  { path: '/pnl', label: 'P&L', icon: '📈' },
  { path: '/analytics', label: 'Analytics', icon: '📉' },
  { path: '/risk', label: 'Risk', icon: '⚠️' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
];

export default function Layout({ children }: LayoutProps) {
  const { data: overview } = useQuery({
    queryKey: ['overview'],
    queryFn: fetchOverview,
  });
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen">
      {/* Sidebar */}
      <aside className="w-56 bg-surface border-r border-gray-700 flex flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-gray-700">
          <h1 className="text-xl font-bold flex items-center gap-2">
            🎲 RLM Dashboard
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md mb-1 transition-colors ${
                  isActive
                    ? 'bg-accent/20 text-accent'
                    : 'text-gray-300 hover:bg-surface-50'
                }`
              }
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Status footer */}
        <div className="p-4 border-t border-gray-700 text-sm text-gray-400">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`w-2 h-2 rounded-full ${
                overview?.bettingEnabled ? 'bg-success' : 'bg-warning'
              }`}
            />
            <span>{overview?.bettingEnabled ? 'Live Mode' : 'Paper Mode'}</span>
          </div>
          <div className="text-xs">
            Auto-refresh: 10s
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col">
        {/* Top bar */}
        <header className="h-14 bg-surface border-b border-gray-700 flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <span
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                overview?.bettingEnabled
                  ? 'bg-success/20 text-success'
                  : 'bg-warning/20 text-warning'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full animate-pulse ${
                  overview?.bettingEnabled ? 'bg-success' : 'bg-warning'
                }`}
              />
              {overview?.bettingEnabled ? 'Live' : 'Paper'}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span>⟳ 10s</span>
            {overview?.lastUpdates.odds && (
              <span title="Last odds update">
                Odds: {formatTime(overview.lastUpdates.odds)}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="px-3 py-1 text-gray-400 hover:text-white hover:bg-surface-50 rounded transition-colors"
              title="Sign out"
            >
              Sign Out
            </button>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 p-6 overflow-auto">{children}</div>
      </main>
    </div>
  );
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  return date.toLocaleTimeString();
}
